import React, { useCallback, useEffect, useState } from "react";
import Business from "../../api/Business";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
    Box,
    Stack,
    Typography,
    Button as MuiButton,
    Skeleton,
    alpha,
    useTheme,
} from "@mui/material";
import BottomButton from "../BottomButton";
import OnboardingShell from "../OnboardingShell";
import { FONTS } from "../../constants/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

const BusinessSelect = () => {
    const { showSnackbar } = useSnackbar();
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selected, setSelected] = useState<Business | undefined>();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchBusinesses = async () => {
            try {
                const businessList = await Business.list();
                setBusinesses(businessList ?? []);

                const storedBusinessId = sessionStorage.getItem("business");
                if (storedBusinessId && businessList) {
                    const selectedBusiness = businessList.find(
                        (b: Business) => b.key === storedBusinessId
                    );
                    setSelected(selectedBusiness);
                }
            } catch (error) {
                console.error("Error fetching businesses:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBusinesses();
    }, []);

    const onSave = useCallback(() => {
        if (selected) {
            sessionStorage.setItem("business", selected.key);
            sessionStorage.setItem("businessName", selected.name);
            window.location.href = "/creation/brand";
        } else {
            showSnackbar("Select a service category", "warning");
        }
    }, [selected, showSnackbar]);

    return (
        <OnboardingShell
            variant="form"
            step={1}
            totalSteps={3}
            heroTitle="Your profession"
            heroSubtitle="For more precise app customization"
            bottomButton={
                selected ? (
                    <BottomButton onClick={onSave} sticky>
                        Go to branding
                    </BottomButton>
                ) : undefined
            }
        >
            <Stack
                className="force-dynamic-translation"
                spacing={1.5}
                sx={{ width: "100%" }}
            >
                {isLoading
                    ? Array.from({ length: 6 }).map((_, i) => (
                          <Skeleton
                              key={i}
                              variant="rounded"
                              height={56}
                              sx={{ borderRadius: "12px" }}
                          />
                      ))
                    : businesses.map((x, i) => (
                          <Item
                              key={x.key}
                              isSelected={selected === x}
                              name={x.name}
                              onClick={() => setSelected(x)}
                              delay={i * 40}
                          />
                      ))}
            </Stack>
        </OnboardingShell>
    );
};

const Item = ({
    name,
    isSelected,
    onClick,
    delay,
}: {
    name: string;
    isSelected: boolean;
    onClick: () => void;
    delay: number;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    return (
        <MuiButton
            variant="text"
            onClick={onClick}
            sx={{
                padding: 0,
                textTransform: "none",
                justifyContent: "flex-start",
                "@keyframes staggerIn": {
                    from: { opacity: 0, transform: "translateY(8px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `staggerIn 0.3s ease-out ${delay}ms both`,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "12px",
                    border: isSelected
                        ? `2px solid ${primary}`
                        : "1px solid rgba(0,0,0,0.06)",
                    bgcolor: isSelected ? alpha(primary, 0.05) : "white",
                    boxShadow: isSelected
                        ? "none"
                        : "0px 1px 3px rgba(0, 0, 0, 0.04)",
                    transition:
                        "border-color 0.2s ease, background-color 0.2s ease",
                }}
            >
                {isSelected ? (
                    <CheckCircleRoundedIcon
                        sx={{ color: primary, fontSize: 24 }}
                    />
                ) : (
                    <RadioButtonUncheckedRoundedIcon
                        sx={{ color: "text.disabled", fontSize: 24 }}
                    />
                )}
                <Typography
                    sx={{
                        fontFamily: FONTS.body,
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: "16px",
                        color: "black",
                    }}
                >
                    {name}
                </Typography>
            </Box>
        </MuiButton>
    );
};

export default BusinessSelect;
